import { FormFieldInputType } from 'models/enum';
import { ChartMetaFormField } from 'models/ui';

export const hasFieldValue = (formField: ChartMetaFormField | undefined): boolean => {
  if (!formField) return false;
  return String(formField.value ?? '').trim().length > 0;
};

export enum FormField {
  DECLARANT_NAME = 'declarantName',
  AGENT_NAME = 'agentName',
  AGENT_ADDRESS = 'agentAddress',
  AGENT_PHONE_NUMBER = 'agentPhoneNumber',
  ALT_AGENT_NAME = 'altAgentName',
  ALT_AGENT_ADDRESS = 'altAgentAddress',
  ALT_AGENT_PHONE_NUMBER = 'altAgentPhoneNumber',
  DECLARATION_NAME_PRINT = 'declarationNamePrint',
  DECLARATION_SIGNATURE = 'declarationSignature',
  WITNESS_1_NAME = 'witness1Name',
  WITNESS_1_ADDRESS = 'witness1Address',
  WITNESS_1_SIGNATURE = 'witness1Signature',
  WITNESS_2_NAME = 'witness2Name',
  WITNESS_2_ADDRESS = 'witness2Address',
  WITNESS_2_SIGNATURE = 'witness2Signature',
  WITNESS_3_NAME = 'witness3Name',
  WITNESS_3_ADDRESS = 'witness3Address',
  WITNESS_3_SIGNATURE = 'witness3Signature'
}

export const PROVIDER_NURSE_FIELD = 'providerNurse';
export const PROVIDER_NURSE_LABEL = 'Provider / Nurse';

export enum SectionTitle {
  GENERAL_INFO = 'General Information',
  SELF_DECLARATION = 'Self Declaration',
  HEALTH_CARE_PROXY = 'Health Care Proxy',
  STATEMENT_OF_DESIRES = 'Statement of Desires and Instructions Regarding Health Care Decisions and Treatments',
  DECLARATION_OF_UNDERSTANDING = 'Declaration of Understanding',
  WITNESSES = 'Statement and Signature of Witnesses',
  ADVANCE_DIRECTIVE = 'Advance Directive'
}

export enum ViewTitle {
  ADVANCE_DIRECTIVE = 'Advance Directive'
}

export const STATIC_TEXT = {
  SELF_DECLARATION_LABEL: `In the following document, "I" and the "declarant" refer to`,
  PHYSICAL_HEALTH_CARE_TITLE: 'Advance Directive for Physical Health Care',
  PHYSICAL_HEALTH_CARE_INTRO:
    'I hereby make known my desire that, should I lose the capacity to make health care decisions, the following instructions take effect regarding consent to or ' +
    'refusal of medical treatment and, if I choose, the designation of my health care agent. I intend that all completed sections of this advance directive be followed.',
  AGENT_SECTION_TITLE: 'A. Appointment of a Health Care Agent',
  AGENT_SECTION_INTRO:
    'I hereby appoint the following individual as my health care agent to make any and all health care decisions for me, except to the extent that I state ' +
    'otherwise. This health care proxy shall take effect when and if I become unable to make my own health care decisions.',
  ALT_AGENT_SECTION_TITLE: 'B. Alternative Health Care Agent (optional)',
  ALT_AGENT_SECTION_INTRO:
    'If the person appointed is unable or unwilling to serve as my health care agent, I hereby appoint the following individual to act as my alternative health care agent.',
  GENERAL_INTENT_TITLE: 'A. General Intent',
  GENERAL_INTENT_ITEMS: [
    'If at any time I should have an incurable condition caused by injury, disease, or illness certified to be a terminal condition by two physicians, and ' +
      'where the application of life-sustaining procedures would serve only to artificially prolong the moment of my death, and where my attending ' +
      'physician determines that my death is imminent whether or not life-sustaining procedures are utilized, I direct that such procedures be withheld or ' +
      'withdrawn and that I be permitted to die naturally.',
    'In the absence of my ability to give directions regarding the use of such life-sustaining procedures, it is my intention that this directive be honored by ' +
      'my agent, my family, and my physicians as the final expression of my legal right to refuse medical or surgical treatment and to accept the ' +
      'consequences from such refusal.',
    'I hereby grant to my agent full authority to make decisions for me regarding my health and personal care. In exercising this authority, my agent shall ' +
      "follow my desires as stated in this document. My agent's authority to interpret my desires is intended to be as broad as possible, except for any " +
      'limitations that I specifically state below. Accordingly, my agent is authorized:\n' +
      'a. To consent, refuse, or withdraw consent to any and all types of medical care, treatment, surgical procedures, diagnostic procedures, medication, ' +
      'and use of artificial respiration, nutritional support and hydration, and cardiopulmonary resuscitation and\n' +
      'b. To authorize my admission to or discharge (even against medical advice) from the hospital, nursing home, residential care, or assisted living or ' +
      'similar facility or service.'
  ],
  LIFE_SUSTAINING_TITLE: 'B. Intent Life-Sustaining Treatment',
  LIFE_SUSTAINING_INTRO:
    'With respect to any life-sustaining treatment, which for purposes of this document means my medical procedure or intervention that would serve only to ' +
    'prolong the dying process, I direct the following:',
  LIFE_SUSTAINING_ITEMS: [
    'Grant of Discretion to Agent to Begin or Terminate Treatment\n' +
      'a. To consent to life-sustaining treatment, provided that at all reasonable times thereafter such treatment is subject to continuing review in light of ' +
      'the fact that I do not want life-sustaining treatment begun or continued if my agent, after consulting with my family, physician, or both, believes that ' +
      'the burdens of treatment outweigh the expected benefits. All relevant consideration shall go into the making of such judgments, including, without ' +
      'limitations, relief of suffering, quality of life, possible extension of life, expected nature of life if extended, and expenses involved in the expected care.\n' +
      'b. Even though life-sustaining treatment has been started, my agent is authorized, on the basis of the considerations listed in this proclamation, to ' +
      'have such life-sustaining treatment terminated at any time after consultation with my attending physician or physicians and my family.',
    'Coma or Persistent Vegetative State\n' +
      'If I have an incurable condition, including but not limited to a coma or persistent vegetative state, that my attending physicians reasonably conclude ' +
      'to be irreversible, I want all life-sustaining treatment stopped immediately, and my agent is authorized to carry out these wishes.',
    'Food and Fluid\n' +
      'I wish to make clear that I intend to include artificial administration of food and fluids among the life-sustaining procedures that may be withheld or ' +
      'withdrawn under the conditions stated above.'
  ],
  DECLARATION_OF_UNDERSTANDING_INTRO:
    'I understand the full intent of this directive and am emotionally and mentally competent to make this directive. I understand that I may revoke this ' +
    'directive at any time.',
  WITNESSES_INTRO_1:
    'The person known to us to be the person whose signature appears at the end of the above directive declared to us, the undersigned, that the above ' +
    'directive, consisting of three pages, including the page that we have signed as witnesses, was his/her directive. He/she then signed the directive in our ' +
    'presence, and, at his/her request in his/her presence and in the presence of each other, we now sign our names as witnesses.',
  WITNESSES_INTRO_2:
    'The declarant has been personally known to us, and we believe him/her to be of sound mind. We are not related to the declarant by blood or marriage, ' +
    "nor would we be entitled to any part of the declarant's estate on the declarant's death; nor are we the attending physicians of the declarant, an " +
    'employee of the attending physician or a health care facility in which the declarant is a patient, or any person who has a claim against any part of the ' +
    "estate of the declarant on the declarant's death."
};

export interface AdvanceDirectiveFieldConfig {
  name: FormField;
  label: string;
  type: FormFieldInputType;
  isRequired?: boolean;
  maxLength?: number;
  placeholder?: string;
  isSignature?: boolean;
}

export interface AdvanceDirectiveStepConfig {
  title: string;
  fields: FormField[];
}

const DEFAULT_TEXT_INPUT_MAX_LENGTH = 150;
const ENTER_NAME_PLACEHOLDER = 'Enter name';
const ENTER_ADDRESS_PLACEHOLDER = 'Enter address';

const withTextInputMaxLength = (fieldConfig: AdvanceDirectiveFieldConfig): AdvanceDirectiveFieldConfig =>
  fieldConfig.type === FormFieldInputType.TEXT_BOX || fieldConfig.type === FormFieldInputType.TEXT_AREA
    ? { ...fieldConfig, maxLength: fieldConfig.maxLength ?? DEFAULT_TEXT_INPUT_MAX_LENGTH }
    : fieldConfig;

export const FIELD_CONFIGS: AdvanceDirectiveFieldConfig[] = [
  { name: FormField.DECLARANT_NAME, label: STATIC_TEXT.SELF_DECLARATION_LABEL, type: FormFieldInputType.TEXT_BOX, isRequired: true, placeholder: 'Enter Name' },
  { name: FormField.AGENT_NAME, label: 'Name', type: FormFieldInputType.TEXT_BOX, isRequired: true, placeholder: ENTER_NAME_PLACEHOLDER },
  { name: FormField.AGENT_ADDRESS, label: 'Address', type: FormFieldInputType.TEXT_AREA, isRequired: true, placeholder: ENTER_ADDRESS_PLACEHOLDER },
  { name: FormField.AGENT_PHONE_NUMBER, label: 'Phone Number', type: FormFieldInputType.TEXT_BOX, isRequired: true, placeholder: 'Enter number' },
  { name: FormField.ALT_AGENT_NAME, label: 'Name', type: FormFieldInputType.TEXT_BOX, placeholder: ENTER_NAME_PLACEHOLDER },
  { name: FormField.ALT_AGENT_ADDRESS, label: 'Address', type: FormFieldInputType.TEXT_AREA, placeholder: ENTER_ADDRESS_PLACEHOLDER },
  { name: FormField.ALT_AGENT_PHONE_NUMBER, label: 'Phone Number', type: FormFieldInputType.TEXT_BOX, placeholder: 'Enter number' },
  { name: FormField.DECLARATION_NAME_PRINT, label: 'Name (Print)', type: FormFieldInputType.TEXT_BOX, placeholder: ENTER_NAME_PLACEHOLDER },
  { name: FormField.DECLARATION_SIGNATURE, label: 'Signature', type: FormFieldInputType.TEXT_BOX, isSignature: true },
  { name: FormField.WITNESS_1_NAME, label: 'Name', type: FormFieldInputType.TEXT_BOX, placeholder: ENTER_NAME_PLACEHOLDER },
  { name: FormField.WITNESS_1_ADDRESS, label: 'Address', type: FormFieldInputType.TEXT_AREA, placeholder: ENTER_ADDRESS_PLACEHOLDER },
  { name: FormField.WITNESS_1_SIGNATURE, label: 'Signature', type: FormFieldInputType.TEXT_BOX, isSignature: true },
  { name: FormField.WITNESS_2_NAME, label: 'Name', type: FormFieldInputType.TEXT_BOX, placeholder: ENTER_NAME_PLACEHOLDER },
  { name: FormField.WITNESS_2_ADDRESS, label: 'Address', type: FormFieldInputType.TEXT_AREA, placeholder: ENTER_ADDRESS_PLACEHOLDER },
  { name: FormField.WITNESS_2_SIGNATURE, label: 'Signature', type: FormFieldInputType.TEXT_BOX, isSignature: true },
  { name: FormField.WITNESS_3_NAME, label: 'Name', type: FormFieldInputType.TEXT_BOX, placeholder: ENTER_NAME_PLACEHOLDER },
  { name: FormField.WITNESS_3_ADDRESS, label: 'Address', type: FormFieldInputType.TEXT_AREA, placeholder: ENTER_ADDRESS_PLACEHOLDER },
  { name: FormField.WITNESS_3_SIGNATURE, label: 'Signature', type: FormFieldInputType.TEXT_BOX, isSignature: true }
].map(withTextInputMaxLength);

export const FIELD_CONFIG_MAP = new Map(FIELD_CONFIGS.map((fieldConfig) => [fieldConfig.name, fieldConfig]));

export const STEP_CONFIGS: AdvanceDirectiveStepConfig[] = [
  {
    title: SectionTitle.HEALTH_CARE_PROXY,
    fields: [FormField.AGENT_NAME, FormField.AGENT_ADDRESS, FormField.AGENT_PHONE_NUMBER, FormField.ALT_AGENT_NAME, FormField.ALT_AGENT_ADDRESS, FormField.ALT_AGENT_PHONE_NUMBER]
  },
  {
    title: SectionTitle.STATEMENT_OF_DESIRES,
    fields: []
  },
  {
    title: SectionTitle.DECLARATION_OF_UNDERSTANDING,
    fields: [FormField.DECLARATION_NAME_PRINT, FormField.DECLARATION_SIGNATURE]
  },
  {
    title: SectionTitle.WITNESSES,
    fields: [
      FormField.WITNESS_1_NAME,
      FormField.WITNESS_1_ADDRESS,
      FormField.WITNESS_1_SIGNATURE,
      FormField.WITNESS_2_NAME,
      FormField.WITNESS_2_ADDRESS,
      FormField.WITNESS_2_SIGNATURE,
      FormField.WITNESS_3_NAME,
      FormField.WITNESS_3_ADDRESS,
      FormField.WITNESS_3_SIGNATURE
    ]
  }
];

// Fields rendered outside the step flow but required before the first step can be completed
export const STEP_REQUIRED_EXTRAS: FormField[][] = [[FormField.DECLARANT_NAME], [], [], []];

// Unique saved-chart titles to avoid deduplication in cookSectionRecords (EditChart.tsx)
export const SAVED_TITLE_MAP: Partial<Record<FormField, string>> = {
  [FormField.DECLARANT_NAME]: 'Declarant',
  [FormField.AGENT_NAME]: 'Agent Name',
  [FormField.AGENT_ADDRESS]: 'Agent Address',
  [FormField.AGENT_PHONE_NUMBER]: 'Agent Phone Number',
  [FormField.ALT_AGENT_NAME]: 'Alternative Agent Name',
  [FormField.ALT_AGENT_ADDRESS]: 'Alternative Agent Address',
  [FormField.ALT_AGENT_PHONE_NUMBER]: 'Alternative Agent Phone Number',
  [FormField.WITNESS_1_NAME]: 'Witness 1 Name',
  [FormField.WITNESS_1_ADDRESS]: 'Witness 1 Address',
  [FormField.WITNESS_1_SIGNATURE]: 'Witness 1 Signature',
  [FormField.WITNESS_2_NAME]: 'Witness 2 Name',
  [FormField.WITNESS_2_ADDRESS]: 'Witness 2 Address',
  [FormField.WITNESS_2_SIGNATURE]: 'Witness 2 Signature',
  [FormField.WITNESS_3_NAME]: 'Witness 3 Name',
  [FormField.WITNESS_3_ADDRESS]: 'Witness 3 Address',
  [FormField.WITNESS_3_SIGNATURE]: 'Witness 3 Signature'
};

// Static declaration text surfaced in the Saved Special Chart, grouped so each block sits under its section header.
export const SAVED_STATEMENTS = {
  PHYSICAL_HEALTH_CARE: STATIC_TEXT.PHYSICAL_HEALTH_CARE_INTRO,
  AGENT_APPOINTMENT: `${STATIC_TEXT.AGENT_SECTION_TITLE}\n\n${STATIC_TEXT.AGENT_SECTION_INTRO}`,
  ALT_AGENT_APPOINTMENT: `${STATIC_TEXT.ALT_AGENT_SECTION_TITLE}\n\n${STATIC_TEXT.ALT_AGENT_SECTION_INTRO}`,
  GENERAL_INTENT: [STATIC_TEXT.GENERAL_INTENT_TITLE, ...STATIC_TEXT.GENERAL_INTENT_ITEMS].join('\n\n'),
  LIFE_SUSTAINING: [STATIC_TEXT.LIFE_SUSTAINING_TITLE, STATIC_TEXT.LIFE_SUSTAINING_INTRO, ...STATIC_TEXT.LIFE_SUSTAINING_ITEMS].join('\n\n'),
  DECLARATION_OF_UNDERSTANDING: STATIC_TEXT.DECLARATION_OF_UNDERSTANDING_INTRO,
  WITNESSES: `${STATIC_TEXT.WITNESSES_INTRO_1}\n\n${STATIC_TEXT.WITNESSES_INTRO_2}`
};
